// Catalog.ID Client — XRPL Master Key (seed encoding + address derivation)
// ============================================================================
// The identity's XRPL master signing key: generated at registration together
// with the other account keys and stored in the key bag as `xrpl_master`
// (round 5, 2026-09-03). The bag entry's private material is the raw 16-byte
// family-seed ENTROPY (base64, like every other entry stores raw key bytes);
// the printed "Secret" is its base58 family-seed encoding ("sEd..."), and the
// printed "Address" is derived from it here.
//
// Ed25519, matching the XRPL wallet module (apps/xrpl — xrpl.js's modern
// default; its C# twin is XrplService.Crypto.XrplKeys, and the golden vectors
// in xrpl.test.ts are shared between the two). The key is BAG-ONLY: publishing
// its public half as an `accountpublickey` row would link the CID account to
// the XRPL address for anyone reading the public key surface, so the server's
// consistency check skips it (KeyBagEntryRepository.BagOnlyKeyIds) and the
// client filter agrees (BAG_ONLY_KEY_IDS in keybag.ts).
//
// The derivation chain mirrors ripple-keypairs: entropy → SHA-512(entropy)
// first half as the Ed25519 seed → public key → 0xED-prefixed →
// RIPEMD-160(SHA-256(...)) account id → base58check classic address.

import { ed25519 } from '@noble/curves/ed25519.js';
import { sha256, sha512 } from '@noble/hashes/sha2.js';
import { ripemd160 } from '@noble/hashes/legacy.js';

/** The key bag slot — mirrors KnownKeyTypes.XrplMaster server-side. */
export const XRPL_MASTER_KEY_ID = 'xrpl_master';

/** The algorithm tag the server requires for that slot. Plain Ed25519 — XRPL
 *  has no post-quantum hybrid, so this key is the one non-hybrid keypair in
 *  the bag. */
export const XRPL_MASTER_ALGORITHM = 'Ed25519';

export const XRPL_SEED_ENTROPY_LEN = 16;

// XRPL's base58 alphabet (r first — a legacy of the ripple address format).
const ALPHABET = 'rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz';

// Family-seed version prefix for ed25519 seeds → encodes as "sEd...".
const ED25519_SEED_PREFIX = [0x01, 0xe1, 0x4b];
// Ed25519 public-key prefix byte used before RIPEMD-160(SHA-256(pub)).
const ED25519_PUBKEY_PREFIX = 0xed;
// "r..." classic-address version byte.
const CLASSIC_ADDRESS_PREFIX = 0x00;

/** Fresh 16-byte family-seed entropy — the value stored in the key bag. */
export function generateXrplSeedEntropy(): Uint8Array {
    const entropy = new Uint8Array(XRPL_SEED_ENTROPY_LEN);
    crypto.getRandomValues(entropy);
    return entropy;
}

/** Encode 16-byte entropy as an XRPL ed25519 family seed ("sEd..."). */
export function encodeXrplSeed(entropy: Uint8Array): string {
    if (entropy.length !== XRPL_SEED_ENTROPY_LEN) {
        throw new Error(`XRPL seed entropy must be exactly ${XRPL_SEED_ENTROPY_LEN} bytes`);
    }
    return base58CheckEncode(new Uint8Array([...ED25519_SEED_PREFIX, ...entropy]));
}

/** Decode an "sEd..." family seed back to its 16-byte entropy. */
export function decodeXrplSeed(seed: string): Uint8Array {
    const payload = base58CheckDecode(seed);
    if (payload.length !== ED25519_SEED_PREFIX.length + XRPL_SEED_ENTROPY_LEN) {
        throw new Error(`Unexpected XRPL seed payload length (${payload.length})`);
    }
    if (ED25519_SEED_PREFIX.some((b, i) => payload[i] !== b)) {
        throw new Error('Not an ed25519 family seed (wrong version prefix)');
    }
    return payload.slice(ED25519_SEED_PREFIX.length);
}

/** Derive the classic address ("r...") from 16-byte seed entropy. */
export function deriveXrplAddress(entropy: Uint8Array): string {
    if (entropy.length !== XRPL_SEED_ENTROPY_LEN) {
        throw new Error(`XRPL seed entropy must be exactly ${XRPL_SEED_ENTROPY_LEN} bytes`);
    }
    // Ed25519 secret seed = first 32 bytes of SHA-512(entropy) — the XRPL
    // convention (ripple-keypairs; XrplKeys.DeriveFromSeed does the same).
    const secretSeed = sha512(entropy).slice(0, 32);
    const publicKey = ed25519.getPublicKey(secretSeed);
    const prefixedPub = new Uint8Array([ED25519_PUBKEY_PREFIX, ...publicKey]);
    const accountId = ripemd160(sha256(prefixedPub));
    return base58CheckEncode(new Uint8Array([CLASSIC_ADDRESS_PREFIX, ...accountId]));
}

// ── Base58Check (XRPL alphabet) ─────────────────────────────────────────────

function checksumFirst4(payload: Uint8Array): Uint8Array {
    return sha256(sha256(payload)).slice(0, 4);
}

function base58CheckEncode(payload: Uint8Array): string {
    return base58Encode(new Uint8Array([...payload, ...checksumFirst4(payload)]));
}

function base58CheckDecode(s: string): Uint8Array {
    const decoded = base58Decode(s);
    if (decoded.length < 4) throw new Error('Base58Check payload too short');
    const payload = decoded.slice(0, -4);
    const expected = checksumFirst4(payload);
    for (let i = 0; i < 4; i++) {
        if (decoded[payload.length + i] !== expected[i]) {
            throw new Error('Base58Check checksum mismatch');
        }
    }
    return payload;
}

function base58Encode(input: Uint8Array): string {
    if (input.length === 0) return '';
    let n = 0n;
    for (const byte of input) n = n * 256n + BigInt(byte);
    let out = '';
    while (n > 0n) {
        out = ALPHABET[Number(n % 58n)] + out;
        n /= 58n;
    }
    // Leading zero bytes → leading alphabet[0] characters.
    for (let i = 0; i < input.length && input[i] === 0; i++) out = ALPHABET[0] + out;
    return out;
}

function base58Decode(s: string): Uint8Array {
    if (s.length === 0) return new Uint8Array(0);
    let n = 0n;
    for (const c of s) {
        const idx = ALPHABET.indexOf(c);
        if (idx < 0) throw new Error(`Invalid base58 character '${c}'`);
        n = n * 58n + BigInt(idx);
    }
    const digits: number[] = [];
    while (n > 0n) {
        digits.unshift(Number(n % 256n));
        n /= 256n;
    }
    let leadingZeros = 0;
    for (const c of s) {
        if (c === ALPHABET[0]) leadingZeros++;
        else break;
    }
    return new Uint8Array([...new Array<number>(leadingZeros).fill(0), ...digits]);
}
